/**
 * /admin/platform/llm/config — primary + fallback LLM configuration.
 *
 * Two cards (PRIMARY, FALLBACK), each editable inline with provider /
 * model / API key / enabled. Saving any card triggers the MFA prompt
 * (the server requires X-CVP-MFA-Code on PUT). A "Test" button on
 * each card runs a 1-message smoke test and shows the verdict.
 *
 * The plaintext API key is only ever held in this component's local
 * state while the operator is editing; on submit it is sent to the
 * server and forgotten. The GET shape returns only a four-character
 * fingerprint so the operator can see "configured" without ever
 * round-tripping a usable secret.
 */

import { type FormEvent, useEffect, useState } from "react";
import {
  adminApi,
  type AdminApiError,
  type LlmConfigTestResult,
  type LlmProviderConfigOut,
} from "./api";
import { MfaPrompt } from "./MfaPrompt";

type Role = "primary" | "fallback";
type Provider = "anthropic" | "openai";

interface CardState {
  provider: Provider;
  model_id: string;
  api_key: string;
  enabled: boolean;
}

const ROLES: Role[] = ["primary", "fallback"];

const ROLE_LABEL: Record<Role, string> = {
  primary: "Primary LLM",
  fallback: "Fallback LLM",
};

const ROLE_BLURB: Record<Role, string> = {
  primary:
    "Tried first on every chat request. Configure the model the platform should use day-to-day.",
  fallback:
    "Used only when the primary fails (network, rate-limit, auth). The next request always re-tries the primary first; there is no sticky fallback state.",
};

const DEFAULT_MODELS: Record<Provider, string> = {
  anthropic: "claude-sonnet-4-6",
  openai: "gpt-4o-mini",
};

function emptyCard(role: Role): CardState {
  return {
    provider: role === "primary" ? "anthropic" : "openai",
    model_id: DEFAULT_MODELS[role === "primary" ? "anthropic" : "openai"],
    api_key: "",
    enabled: true,
  };
}

export function LlmConfigPage() {
  const [stored, setStored] = useState<Record<Role, LlmProviderConfigOut | null>>({
    primary: null,
    fallback: null,
  });
  const [drafts, setDrafts] = useState<Record<Role, CardState>>({
    primary: emptyCard("primary"),
    fallback: emptyCard("fallback"),
  });
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pendingSave, setPendingSave] = useState<Role | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Role | null>(null);
  const [testResult, setTestResult] = useState<
    Record<Role, LlmConfigTestResult | null>
  >({ primary: null, fallback: null });
  const [testBusy, setTestBusy] = useState<Role | null>(null);

  async function reload() {
    try {
      const result = await adminApi.listLlmConfigs();
      const next: Record<Role, LlmProviderConfigOut | null> = {
        primary: null,
        fallback: null,
      };
      for (const item of result.items) {
        next[item.role] = item;
      }
      setStored(next);
      setDrafts({
        primary: next.primary
          ? {
              provider: next.primary.provider,
              model_id: next.primary.model_id,
              api_key: "",
              enabled: next.primary.enabled,
            }
          : emptyCard("primary"),
        fallback: next.fallback
          ? {
              provider: next.fallback.provider,
              model_id: next.fallback.model_id,
              api_key: "",
              enabled: next.fallback.enabled,
            }
          : emptyCard("fallback"),
      });
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Could not load LLM config.");
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  function onSubmit(role: Role, e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const draft = drafts[role];
    if (!stored[role] && draft.api_key.trim().length === 0) {
      setError(`API key is required to save ${ROLE_LABEL[role]}.`);
      return;
    }
    setPendingSave(role);
  }

  async function executeSave(code: string) {
    if (pendingSave === null) {
      return;
    }
    const role = pendingSave;
    const draft = drafts[role];
    try {
      // If the operator left api_key blank for an existing row,
      // we send the key from a placeholder dot-string the server
      // will reject — instead, skip save and warn.
      if (draft.api_key.trim().length === 0) {
        setPendingSave(null);
        setError(
          "Re-enter the API key to save changes. The stored ciphertext is not retrievable.",
        );
        return;
      }
      await adminApi.putLlmConfig(
        role,
        {
          provider: draft.provider,
          api_key: draft.api_key,
          model_id: draft.model_id,
          enabled: draft.enabled,
        },
        code,
      );
      setPendingSave(null);
      setInfo(`${ROLE_LABEL[role]} saved.`);
      await reload();
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      throw new Error(apiErr.message ?? "Save failed.");
    }
  }

  async function executeDelete(code: string) {
    if (pendingDelete === null) {
      return;
    }
    const role = pendingDelete;
    try {
      await adminApi.deleteLlmConfig(role, code);
      setPendingDelete(null);
      setInfo(`${ROLE_LABEL[role]} cleared.`);
      await reload();
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      throw new Error(apiErr.message ?? "Delete failed.");
    }
  }

  async function runTest(role: Role) {
    setTestBusy(role);
    setError(null);
    setInfo(null);
    try {
      const result = await adminApi.testLlmConfig(role, "ping");
      setTestResult((prev) => ({ ...prev, [role]: result }));
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Test failed.");
    } finally {
      setTestBusy(null);
    }
  }

  if (!loaded) {
    return <div className="admin-page">Loading…</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h2>LLM provider configuration</h2>
        <p className="admin-page-blurb">
          Configure the primary LLM the platform calls on every request, plus
          an optional fallback that handles transient primary outages. Each
          configuration change requires a fresh authenticator code.
        </p>
      </header>
      {error !== null && <div className="banner banner-error">{error}</div>}
      {info !== null && <div className="banner banner-info">{info}</div>}

      <div className="card-stack">
        {ROLES.map((role) => {
          const draft = drafts[role];
          const cur = stored[role];
          const test = testResult[role];
          return (
            <section key={role} className="admin-card">
              <header className="admin-card-head">
                <div>
                  <div className="admin-card-title">{ROLE_LABEL[role]}</div>
                  <div className="admin-card-blurb">{ROLE_BLURB[role]}</div>
                </div>
                <div className="admin-card-status">
                  {cur ? (
                    <>
                      <span className={`pill pill-${cur.enabled ? "ok" : "off"}`}>
                        {cur.enabled ? "Configured" : "Disabled"}
                      </span>
                      <span className="admin-card-meta">
                        {cur.provider} · {cur.model_id} · key {cur.key_fingerprint}
                      </span>
                    </>
                  ) : (
                    <span className="pill pill-off">Not configured</span>
                  )}
                </div>
              </header>

              <form onSubmit={(e) => onSubmit(role, e)} className="admin-card-body">
                <div className="field-row">
                  <div className="field">
                    <label htmlFor={`${role}-provider`}>Provider</label>
                    <select
                      id={`${role}-provider`}
                      value={draft.provider}
                      onChange={(e) => {
                        const provider = e.target.value as Provider;
                        setDrafts((prev) => ({
                          ...prev,
                          [role]: {
                            ...prev[role],
                            provider,
                            model_id: DEFAULT_MODELS[provider],
                          },
                        }));
                      }}
                    >
                      <option value="anthropic">Anthropic</option>
                      <option value="openai">OpenAI</option>
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor={`${role}-model`}>Model id</label>
                    <input
                      id={`${role}-model`}
                      type="text"
                      value={draft.model_id}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [role]: { ...prev[role], model_id: e.target.value },
                        }))
                      }
                      placeholder={DEFAULT_MODELS[draft.provider]}
                      required
                    />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor={`${role}-key`}>API key</label>
                  <input
                    id={`${role}-key`}
                    type="password"
                    value={draft.api_key}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [role]: { ...prev[role], api_key: e.target.value },
                      }))
                    }
                    placeholder={
                      cur
                        ? "Re-enter to update (stored value is not retrievable)"
                        : "sk-... or equivalent"
                    }
                    autoComplete="off"
                  />
                </div>
                <div className="field-inline">
                  <input
                    id={`${role}-enabled`}
                    type="checkbox"
                    checked={draft.enabled}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [role]: { ...prev[role], enabled: e.target.checked },
                      }))
                    }
                  />
                  <label htmlFor={`${role}-enabled`}>Enabled</label>
                </div>
                <div className="actions">
                  <button type="submit" className="btn btn-primary btn-sm">
                    Save {ROLE_LABEL[role].toLowerCase()}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={testBusy === role || !cur}
                    onClick={() => runTest(role)}
                  >
                    {testBusy === role ? "Testing…" : "Test"}
                  </button>
                  {cur && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => setPendingDelete(role)}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </form>

              {test !== null && (
                <div
                  className={`admin-card-foot ${
                    test.ok ? "card-foot-ok" : "card-foot-err"
                  }`}
                >
                  {test.ok ? (
                    <>
                      <strong>OK</strong> in {test.latency_ms} ms · in:{" "}
                      {test.input_tokens} tok · out: {test.output_tokens} tok
                      {test.sample_text ? (
                        <div className="admin-card-sample">
                          “{test.sample_text}”
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <strong>Failed</strong> {test.error}
                    </>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <MfaPrompt
        open={pendingSave !== null}
        title={`Save ${pendingSave ? ROLE_LABEL[pendingSave].toLowerCase() : ""}`}
        description="Sensitive change. Enter your six-digit authenticator code to confirm."
        onCancel={() => setPendingSave(null)}
        onConfirm={executeSave}
      />
      <MfaPrompt
        open={pendingDelete !== null}
        title={`Clear ${pendingDelete ? ROLE_LABEL[pendingDelete].toLowerCase() : ""}`}
        description="Removes the stored configuration. Calls will fall through to the env-var floor until you reconfigure."
        onCancel={() => setPendingDelete(null)}
        onConfirm={executeDelete}
      />
    </div>
  );
}
